import type { Address } from 'viem'

import type { FeedRow, LegProof, OracleFeeds, Side } from './oracleFeeds'

import { FEED_TYPE_LABEL, LEG_CODE, SIDE_LABEL } from './oracleFeeds'

const FIXED_ONE = FEED_TYPE_LABEL.fixedOne

export interface FeedSideGroup {
  side: Side
  label: string
  symbol: string | null
  logo: string | null
  rows: FeedRow[]
}

/** One oracle branch (Feed 1 or Feed 2 on a two-feed oracle, untitled otherwise) and its two sides. */
export interface FeedBranchGroup {
  id: string
  title: string | null
  /** Nodes outside any feed shown for their admin, such as the router; above the sides. */
  oracleRows: FeedRow[]
  sides: FeedSideGroup[]
}

/** A side with no feed contract: the oracle prices it at a fixed 1. Only its name and type show. */
const fixedOneRow = (id: string, name: string, proof: LegProof | null): FeedRow => ({
  id,
  name,
  listedAs: null,
  address: null,
  type: FIXED_ONE,
  provider: null,
  providerType: null,
  feedUrl: null,
  admins: null,
  heartbeat: null,
  heartbeatAbsence: null,
  deviation: null,
  price: null,
  age: null,
  terms: null,
  proof,
  pair: null,
  children: []
})

export const isFixedOne = (row: FeedRow) => row.type === FIXED_ONE

/** A side with no feed contract; a deployed constant feed shows its provider and admin instead. */
export const isEmptySlot = (row: FeedRow) => isFixedOne(row) && row.address === null

/** Vault slots return 1 through `VaultLib`, feed slots through the feed lib. Only slots the decoder reported as unset get a proof. */
function legProof(oracle: Address | null, unset: string[]): LegProof | null {
  if (!oracle || unset.length === 0) return null
  const vaultOnly = unset.every(slot => slot.endsWith('vault'))
  return { oracle, code: vaultOnly ? LEG_CODE.vault : LEG_CODE.feed }
}

/**
 * A two-feed oracle swaps its whole first or second oracle, so each branch groups its own
 * Collateral and Loan. Both sides always show; a side with no feed contract shows one Fixed 1:1
 * row named after its asset.
 */
export function groupByBranch(
  feeds: OracleFeeds,
  symbols: Record<Side, string | null>,
  logos: Partial<Record<Side, string | null>> = {}
): FeedBranchGroup[] {
  return feeds.branches.map(branch => ({
    id: branch.id,
    title: branch.title,
    oracleRows: branch.oracleRows,
    sides: branch.sections.map(({ side, rows }) => ({
      side,
      label: symbols[side] ? `${SIDE_LABEL[side]} · ${symbols[side]}` : SIDE_LABEL[side],
      symbol: symbols[side],
      logo: logos[side] ?? null,
      // A lone fixed feed the API gives no base / quote takes its side's asset name.
      rows: rows.length
        ? rows.length === 1 && isFixedOne(rows[0]!) && rows[0]!.listedAs === null && symbols[side]
          ? [{ ...rows[0]!, name: symbols[side] }]
          : rows
        : [
            fixedOneRow(
              `${branch.id}-${side}-fixed`,
              symbols[side] ?? SIDE_LABEL[side],
              legProof(branch.oracle, branch.unset[side])
            )
          ]
    }))
  }))
}
